import { FC, useEffect, useRef, useState } from "react";
import classNames from "classnames";

import { useMarginaliaContext } from "../MarginaliaContext/MarginaliaContext";
import { ChartLabel } from "../ChartLabel/ChartLabel";
import { TextWithChartBlockProps } from "./TextWithChartBlock.type";

const TextWithChartBlock: FC<TextWithChartBlockProps> = ({ id, data, chartLabel }) => {
	const { text, align = "justify", title, component: Component, props: componentProps, chartPosition = "right", textVerticalAlign = "top" } = data;

	const { updateBlockEl } = useMarginaliaContext();
	const containerRef = useRef<HTMLDivElement>(null);
	const chartColRef = useRef<HTMLDivElement>(null);
	const [isNarrow, setIsNarrow] = useState(false);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const observer = new ResizeObserver(([entry]) => {
			setIsNarrow(entry.contentRect.width < 650);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		updateBlockEl(id, chartColRef.current);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isNarrow]);

	const paragraphs = Array.isArray(text) ? text : [text];

	const textColumn = (
		<div
			className={classNames("flex flex-col", {
				"md:w-2/5": !isNarrow,
				// Centering only applies side-by-side; stacked, the text column has no spare height to centre in.
				"justify-center": textVerticalAlign === "center" && !isNarrow,
				"justify-start": textVerticalAlign === "top" || isNarrow,
			})}
		>
			{typeof title === "string" && title.trim().length > 0 && (
				<h3 className="leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: title }} />
			)}
			{paragraphs.map((paragraph, index) => (
				<p
					key={`${id}-paragraph-${index}`}
					className={classNames("leading-relaxed", { "mb-6": index + 1 !== paragraphs.length })}
					style={{ textAlign: align }}
					dangerouslySetInnerHTML={{ __html: paragraph }}
				/>
			))}
		</div>
	);

	const chartColumn = (
		<div ref={chartColRef} className={classNames({ "md:w-3/5": !isNarrow })}>
			<ChartLabel label={chartLabel} className="mb-1" />
			<Component {...componentProps} />
		</div>
	);

	const showTextFirst = isNarrow || chartPosition === "right";

	return (
		<div id={id} ref={containerRef} className={classNames("flex gap-8", isNarrow ? "flex-col" : "flex-row")}>
			{showTextFirst ? textColumn : chartColumn}
			{showTextFirst ? chartColumn : textColumn}
		</div>
	);
};

export { TextWithChartBlock };
